import moment from 'moment';

import { Message } from '../types/chatModels/MangoRoomListResponse';
import { ICommunityMessage } from '../types/communityMessagesTypes';
import { DATE_FORMATS } from './constants';
import { extractTextAndMentionsMap } from './utils';

export const getDisplayMessage = (message: Message) => {
  if (message.isDeleted) {
    return 'This message was deleted';
  }

  if (message.message) return message.message;

  let displayText = '';

  switch (message.messageType) {
    case 'image':
      displayText = 'sent an image';
      break;

    case 'video':
      displayText = 'sent a video';
      break;

    case 'audio':
      displayText = 'sent an audio';
      break;

    case 'doc':
      displayText = 'sent a document';
      break;

    default:
      displayText = 'sent an attachment';
  }

  return displayText;
};

export type ChatAttachmentTypes = '' | 'image' | 'video' | 'audio' | 'doc';

export const getTimeDifferenceInDays = (time: string | Date) => {
  const currentDate = moment();
  const givenDate = moment(time);

  const isToday = givenDate.isSame(moment(), 'day');

  const isYesterday = givenDate.isSame(moment().subtract(1, 'days'), 'day');

  if (isToday) return givenDate.format('h:mm A');

  if (isYesterday) return 'Yesterday';

  if (currentDate.diff(givenDate, 'days') <= 6) {
    return givenDate.format('dddd');
  }

  return givenDate.format('DD/MM/YYYY');
};

export const getDisplayMessageInPlainText = (
  message: ICommunityMessage,
): string => {
  if (message?.isDeleted) {
    return '🚫 This message was deleted.';
  }

  if (message?.poll) {
    const question = message?.pollMetadata?.question?.trim();
    return `📊 ${question || 'Poll'}`;
  }

  const firstFile =
    message?.assets && message.assets.length > 0 ? message.assets[0] : null;

  if (message.message.trim() && !firstFile) {
    return extractTextAndMentionsMap(message.message).text;
  }

  if (!firstFile) return '';

  let emoji = '';
  let displayText = message.message.trim() || '';

  switch (firstFile.fileType) {
    case 'image':
      emoji = '📷';
      displayText = displayText || 'Photo';
      break;

    case 'video':
      emoji = '🎥';
      displayText = displayText || 'Video';
      break;

    case 'audio':
      emoji = '🎧';
      displayText = displayText || 'Audio';
      break;

    default:
      emoji = '📎';
      displayText = displayText || firstFile.fileName || 'Attachment';
  }

  return `${emoji} ${displayText}`;
};

/**
 * Returns the readable date format for the given date.
 * @param date - The date to get the readable date format for.
 * @returns The readable date format for the given date.
 * @example
 * getReadableDateFormat('2021-01-01'); // 'Today'
 * getReadableDateFormat('2021-01-02'); // 'Yesterday'
 * getReadableDateFormat('2021-01-03'); // 'Monday' (if days difference is less than or equal to 6 showing the day in this week)
 * getReadableDateFormat('2021-01-03'); // 'Mon, 1 Jan' (if days difference is more than 6 showing the date in the format of ddd, D MMM)
 * getReadableDateFormat('2021-01-04'); // '04/01/2021' (if date is in different year showing the date in the format of DD/MM/YYYY)
 */
export const getReadableDateFormat = (date: string | Date) => {
  const dayMoment = moment(date).startOf('day');
  const daysDifference = moment().startOf('day').diff(dayMoment, 'day');

  if (daysDifference === 0) return 'Today';

  if (daysDifference === 1) return 'Yesterday';

  if (daysDifference <= 6) return dayMoment.format('dddd').toTitleCase();

  if (moment().isSame(dayMoment, 'year')) {
    return dayMoment.format(DATE_FORMATS.READABLE_DAY_FORMAT);
  }

  return dayMoment.format(DATE_FORMATS.DEFAULT_DATE_FORMAT);
};

/**
 * Returns the formatted date and time for the given time.
 * @param time - The time to get the formatted date and time for.
 * @returns The formatted date and time for the given time.
 * @example
 * getFormattedDateTime('2021-01-01 12:00:00'); // '12:00 PM'
 * getFormattedDateTime('2021-01-02 12:00:00'); // 'Yesterday'
 * getFormattedDateTime('2021-01-03 12:00:00'); // 'Monday' (if days difference is less than or equal to 6 it shows the day in this week)
 * getFormattedDateTime('2021-01-04 12:00:00'); // '04/01/2021' (if days difference is greater than 6 it shows the date in the format of DD/MM/YYYY)
 */
export const getFormattedDateTime = (time: string | Date) => {
  const currentDate = moment();
  const givenDate = moment(time);
  const isToday = givenDate.isSame(moment(), 'day');
  const isYesterday = givenDate.isSame(moment().subtract(1, 'days'), 'day');
  const daysDifference = currentDate.diff(givenDate, 'days');

  if (isToday) return givenDate.format('h:mm A');

  if (isYesterday) return 'Yesterday';

  if (daysDifference <= 6) {
    return givenDate.format('dddd');
  }

  return givenDate.format(DATE_FORMATS.DEFAULT_DATE_FORMAT);
};

export const getBlockedMessageForDM = (data: {
  selfStopReceivingMessages?: boolean;
  otherStopReceivingMessages?: boolean;
}): string => {
  if (data?.selfStopReceivingMessages) {
    return 'You stopped receiving messages from this person';
  }

  if (data?.otherStopReceivingMessages) {
    return `You can't send messages to this person`;
  }

  return '';
};
